/**
 * CopyMessageButton — puts one chat message on the clipboard.
 *
 * Copies TWO flavours when the browser allows it:
 *   - text/plain: the original Markdown, which is what a code editor, a
 *     ticket, or another chat window wants.
 *   - text/html: the bubble exactly as rendered, so pasting into Word, Gmail
 *     or a doc keeps the table, the bold and the lists instead of dropping a
 *     wall of pipes and asterisks.
 *
 * The HTML comes from the rendered node rather than from a second Markdown
 * pass: what you paste is then literally what you were looking at, and there
 * is no second parser to disagree with the first.
 *
 * Falls back to plain text where ClipboardItem is unavailable (older Safari,
 * insecure contexts), and reports failure rather than claiming success.
 */
import React, { useCallback, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { theme } from '@turn-connect/ui';

const colors = theme.colors;

interface CopyMessageButtonProps {
  /** The message's original Markdown — the text/plain flavour. */
  markdown: string;
  /** The rendered bubble, used for the text/html flavour. */
  renderedRef?: React.RefObject<HTMLDivElement | null>;
  /** Announced to screen readers, e.g. "Copy the assistant's reply". */
  label?: string;
}

type CopyState = 'idle' | 'copied' | 'failed';

export const CopyMessageButton: React.FC<CopyMessageButtonProps> = ({ markdown, renderedRef, label = 'Copy message' }) => {
  const [state, setState] = useState<CopyState>('idle');
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flash = useCallback((next: CopyState) => {
    setState(next);
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setState('idle'), 1800);
  }, []);

  const copy = useCallback(async () => {
    const html = renderedRef?.current?.innerHTML;
    try {
      if (html && typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': new Blob([markdown], { type: 'text/plain' }),
            'text/html': new Blob([html], { type: 'text/html' }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(markdown);
      }
      flash('copied');
    } catch {
      // Clipboard access can be denied outright (permissions, insecure
      // context). Saying "Copied" when nothing was copied is worse than
      // saying nothing.
      flash('failed');
    }
  }, [markdown, renderedRef, flash]);

  const copied = state === 'copied';
  const failed = state === 'failed';

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={label}
      title={failed ? 'Copy failed — your browser blocked clipboard access' : label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '4px 8px',
        minHeight: 28,
        background: 'transparent',
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        color: copied ? colors.status.success : failed ? colors.status.error : colors.text.tertiary,
        fontFamily: 'inherit',
        fontSize: 11.5,
        fontWeight: 500,
      }}
    >
      {copied ? <Check size={12} aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />}
      <span aria-live="polite">{copied ? 'Copied' : failed ? 'Copy failed' : 'Copy'}</span>
    </button>
  );
};

export default CopyMessageButton;
